import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Package } from 'lucide-react';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { useFetchProductCatalog, useUpdateProduct } from '@/hooksApi/useProductCatalog';
import ProductForm from './ProductForm';
import type { ProductCatalogRequest } from '@/types/productCatalog';

const SettingsProductCatalogEdit = () => {
  const { uid } = useParams<{ uid: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch the list to find the single item — the API has a paginated list endpoint
  // We reuse the list hook with a targeted search and pull the matching item from cache.
  // A dedicated single-item fetch hook is not yet exposed, so we query page 1 with no
  // search, then fall back to the store cache. If the item is not found we show an error.
  const { data, isFetching, isError } = useFetchProductCatalog(1, 100);
  const product = data?.object?.items?.find((p) => p.uid === uid);

  const { mutate: updateProduct, isLoading: isUpdating } = useUpdateProduct(uid ?? '');

  const handleSave = (data: ProductCatalogRequest) => {
    if (!uid) return;
    updateProduct(data, {
      onSuccess: () => {
        toast({
          title: String(t('success_title', { defaultValue: 'Success' })),
          description: String(t('product_updated', { defaultValue: 'Product updated successfully.' })),
        });
        queryClient.invalidateQueries(['product-catalog']);
        navigate('/settings/product-catalog');
      },
      onError: (error: any) => {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          String(t('operation_failed', { defaultValue: 'Operation failed. Please try again.' }));
        toast({
          title: String(t('error_title', { defaultValue: 'Error' })),
          description: msg,
          variant: 'destructive',
        });
      },
    });
  };

  // Loading skeleton
  if (isFetching && !product) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 max-w-3xl w-full mx-auto'>
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-6'>
            <div className='animate-pulse space-y-6'>
              <div className='h-5 bg-sd-muted rounded w-1/3' />
              <div className='space-y-4'>
                <div className='h-10 bg-sd-muted rounded-lg' />
                <div className='grid grid-cols-2 gap-4'>
                  <div className='h-10 bg-sd-muted rounded-lg' />
                  <div className='h-10 bg-sd-muted rounded-lg' />
                </div>
                <div className='h-10 bg-sd-muted rounded-lg' />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error / not found state
  if (isError || (!isFetching && !product)) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 max-w-3xl w-full mx-auto'>
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-10 flex flex-col items-center text-center gap-3'>
            <div className='h-12 w-12 rounded-xl bg-sd-muted flex items-center justify-center'>
              <Package className='h-6 w-6 text-sd-muted-foreground' />
            </div>
            <div className='space-y-1'>
              <p className='text-sm font-medium text-sd-foreground'>
                {String(t('product_not_found', { defaultValue: 'Product not found' }))}
              </p>
              <p className='text-xs text-sd-muted-foreground'>
                {String(
                  t('product_not_found_description', {
                    defaultValue: 'The product you are looking for does not exist or has been deleted.',
                  }),
                )}
              </p>
            </div>
            <button
              onClick={() => navigate('/settings/product-catalog')}
              className='mt-1 px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {String(t('back_to_product_catalog', { defaultValue: 'Back to Product Catalog' }))}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 pb-0 max-w-3xl w-full mx-auto'>
        <SettingsBreadcrumb
          moduleName='product_catalog'
          moduleRoute='/settings/product-catalog'
          action='edit_product'
        />
      </div>
      <ProductForm
        initialData={product}
        onSave={handleSave}
        onCancel={() => navigate('/settings/product-catalog')}
        isLoading={isUpdating}
      />
    </div>
  );
};

export default SettingsProductCatalogEdit;
